/**
 * Guess which client column feeds each kept field, and which units they use,
 * from header names. Always shown to the user to confirm before data checks.
 */
import type { ColumnMapping, KeptField, UploadUnits } from "./schema.ts";

const norm = (h: string) =>
  h
    .toLowerCase()
    .replace(/\(.*?\)|\[.*?\]/g, " ")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

/** Synonyms in priority order; matched exactly first, then as whole words inside the header. */
const SYNONYMS: Record<KeptField, string[]> = {
  orderId: ["order id", "order number", "order no", "order ref", "order reference", "order", "order name", "shipment id", "consignment number", "reference"],
  orderDate: ["order date", "ship date", "despatch date", "dispatch date", "shipped date", "date", "created at", "created"],
  sku: ["sku", "lineitem sku", "variant sku", "product sku", "product code", "item code", "item sku"],
  qty: ["qty", "quantity", "lineitem quantity", "units", "item quantity"],
  weight: ["weight", "parcel weight", "package weight", "total weight", "shipping weight", "weight kg", "weight g"],
  length: ["length", "parcel length", "package length", "l", "dimensions", "parcel dimensions", "package dimensions", "dims"],
  width: ["width", "parcel width", "package width", "w"],
  height: ["height", "parcel height", "package height", "depth", "h"],
  carrierService: ["carrier service", "shipping service", "delivery service", "service", "shipping method", "delivery method", "service code", "carrier"],
  // Not plain "shipping": in Shopify exports that is what the customer paid, not the carrier cost.
  cost: ["shipping cost", "postage cost", "postage", "carriage", "carrier cost", "label cost", "delivery cost", "cost"],
  postcode: ["postcode", "post code", "shipping postcode", "delivery postcode", "ship postcode", "shipping zip", "zip", "zip code", "postal code"],
};

const ORDER: KeptField[] = ["orderId", "orderDate", "sku", "qty", "postcode", "carrierService", "cost", "weight", "length", "width", "height"];

export function guessMapping(headers: string[]): ColumnMapping {
  const mapping: ColumnMapping = {};
  const used = new Set<string>();
  const normalised = headers.map((h) => ({ h, n: norm(h) }));

  for (const pass of ["exact", "words"] as const) {
    for (const field of ORDER) {
      if (mapping[field]) continue;
      for (const syn of SYNONYMS[field]) {
        const hit = normalised.find(({ h, n }) => {
          if (used.has(h)) return false;
          if (pass === "exact") return n === syn;
          // single letters (l, w, h) only ever match exactly
          return syn.length > 2 && ` ${n} `.includes(` ${syn} `);
        });
        if (hit) {
          mapping[field] = hit.h;
          used.add(hit.h);
          break;
        }
      }
    }
  }

  // A single "dimensions" column ("30x20x10") feeds all three sides.
  const dimsCol = mapping.length && /dimension|dims/.test(norm(mapping.length)) ? mapping.length : undefined;
  if (dimsCol && !mapping.width && !mapping.height) {
    mapping.width = dimsCol;
    mapping.height = dimsCol;
  }
  return mapping;
}

/** Guess units from header hints such as "Weight (g)", "Length mm", "Cost inc VAT". */
export function guessUnits(mapping: ColumnMapping): UploadUnits {
  const w = (mapping.weight ?? "").toLowerCase();
  const l = (mapping.length ?? "").toLowerCase();
  const c = (mapping.cost ?? "").toLowerCase();
  const has = (s: string, re: RegExp) => re.test(s);
  return {
    weight: has(w, /\bkg\b|kilo/) ? "kg" : has(w, /\bg\b|gram|\(g\)|_g\b/) ? "g" : has(w, /\blbs?\b|pound/) ? "lb" : has(w, /\boz\b|ounce/) ? "oz" : "kg",
    length: has(l, /\bmm\b|millim/) ? "mm" : has(l, /\bin\b|inch/) ? "in" : "cm",
    costIncludesVat: has(c, /\binc(l|luding)?\.?\s*vat\b|\bgross\b/),
  };
}

export const REQUIRED_FIELDS: KeptField[] = ["orderId", "orderDate", "weight", "length", "width", "height", "carrierService", "cost"];

export function missingRequired(mapping: ColumnMapping): KeptField[] {
  return REQUIRED_FIELDS.filter((f) => !mapping[f]);
}
